// Workstream: accounts. Who is signed in, shared by the top bar, the account page and the pack editor.
import { useEffect, useSyncExternalStore } from 'react';
import { reconnectSocket } from '../../lib/socket';
import { getMyPacks } from '../../lib/storage';
import { accountApi, type AccountUser, type ClaimResult } from './api';

export interface AccountState {
  /** 'unknown' until the first /me answer comes back. */
  status: 'unknown' | 'ready';
  user: AccountUser | null;
}

let state: AccountState = { status: 'unknown', user: null };
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function set(next: AccountState) {
  state = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function refreshAccount(): Promise<void> {
  loading ??= accountApi
    .me()
    .then((r) => set({ status: 'ready', user: r.user }))
    // Offline or server trouble: carry on as whoever we thought it was (usually a guest).
    .catch(() => set({ status: 'ready', user: state.user }))
    .finally(() => {
      loading = null;
    });
  return loading;
}

export function useAccount(): AccountState {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  useEffect(() => {
    if (state.status === 'unknown') void refreshAccount();
  }, []);
  return snapshot;
}

export function setAccountUser(user: AccountUser | null) {
  set({ status: 'ready', user });
}

/** After signing in: attach this device's packs to the account and let open rooms pick up the account. */
export async function afterSignIn(user: AccountUser): Promise<ClaimResult | null> {
  const pairs = getMyPacks()
    .filter((p) => p.code && p.editKey)
    .map((p) => ({ code: p.code, editKey: p.editKey }));
  let claimed: ClaimResult | null = null;
  // Claim before announcing the sign-in, so "My packs" already includes them when it loads.
  if (pairs.length) claimed = await accountApi.claim(pairs.slice(0, 50)).catch(() => null);
  setAccountUser(user);
  reconnectSocket();
  return claimed;
}

export async function signOut(): Promise<void> {
  await accountApi.logout();
  setAccountUser(null);
  reconnectSocket();
}

/** First letter of a name, for the round badge in the top bar. */
export function initial(name: string): string {
  return [...name.trim()][0]?.toUpperCase() ?? '?';
}
